import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Anohana, Btn, Bx, IconBtn, Typo } from '@curry-group/mui-curcuma';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBellSlash, faCopy, faMessageBot, faTimes } from '@fortawesome/pro-light-svg-icons';
import { HeadlineContainer } from '../../headline-container';
import { api } from '../../../data/api';
import { GetCookie } from '../../../helper';
import { ICommunicationAction } from '../header';

function readFlag(content: unknown, key: string): boolean {
  if (!content || typeof content !== 'object') return false;
  return (content as Record<string, unknown>)[key] === true;
}

function readString(content: unknown, key: string): string | undefined {
  if (!content || typeof content !== 'object') return undefined;
  const value = (content as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Header actions of a complaint chat, for the complaint contact only: the bot
 * session (bot icon, opens a popup with the id and a copy button) and the mute
 * toggle. Returns no actions for anyone else or for ordinary chats. Render `modal`
 * somewhere in the same view.
 */
export function useComplaintHeaderActions(): { actions: Array<ICommunicationAction>; modal: ReactNode } {
  const detailContent = useSelector(state => state.detail?.item?.content);
  const detailId = useSelector(state => state.detail?.item?._id);
  const userId = useSelector(state => state.foundation?.profile?.userId);
  const contactUserId = useSelector(state => state.foundation?.complaintContact?.userId);

  const [muted, setMuted] = useState(false);
  const [working, setWorking] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setMuted(readFlag(detailContent, 'muted'));
    setSessionOpen(false);
    setCopied(false);
  }, [detailContent, detailId]);

  const isComplaint = readFlag(detailContent, 'isComplaint');
  const isContact = !!contactUserId && contactUserId === userId;
  const botSessionId = readString(detailContent, 'botSessionId');

  if (!isComplaint || !isContact) {
    return { actions: [], modal: null };
  }

  const toggleMute = async () => {
    if (!detailId) return;
    const next = !muted;
    setWorking(true);
    try {
      const xsrfToken = GetCookie('XSRF-TOKEN');
      const response = await fetch(api.params(api.v2.complaint.mute, { id: detailId }), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(xsrfToken ? { 'X-XSRF-TOKEN': xsrfToken } : {}),
        },
        body: JSON.stringify({ muted: next }),
      });
      if (response.ok) {
        setMuted(next);
      }
    } catch {
      // keep the previous state; the button can simply be pressed again
    }
    setWorking(false);
  };

  const copySession = async () => {
    if (!botSessionId) return;
    try {
      await navigator.clipboard.writeText(botSessionId);
      setCopied(true);
    } catch {
      // clipboard blocked — the id is shown in the popup and can be selected by hand
    }
  };

  const closeSession = () => {
    setSessionOpen(false);
    setCopied(false);
  };

  const actions: Array<ICommunicationAction> = [];
  if (botSessionId) {
    actions.push({
      specificType: 'IconBtn',
      onClick: () => setSessionOpen(true),
      icon: <FontAwesomeIcon icon={faMessageBot} />,
      title: 'Bot-Session anzeigen',
      size: 'small',
    });
  }
  actions.push({
    specificType: 'IconBtn',
    onClick: toggleMute,
    icon: <FontAwesomeIcon icon={muted ? faBell : faBellSlash} />,
    title: muted ? 'Benachrichtigungen wieder aktivieren' : 'Stummschalten',
    size: 'small',
    disabled: working,
    // Filled while muted, so the state is visible at a glance and not only in the icon.
    ...(muted ? { variant: 'contained' as const, color: 'primary' as const } : {}),
  });

  const modal = (
    <Anohana open={sessionOpen} fullWidth maxWidth="sm">
      <HeadlineContainer
        bgcolor="background.paper"
        px={3}
        py={2.5}
        headline="Bot-Session"
        minHeight={81}
        headlineVariant="h4"
        headlineComponent="h5"
        endAdornment={<IconBtn size="small" onClick={closeSession}><FontAwesomeIcon icon={faTimes} /></IconBtn>}
      />
      <Bx py={3} px={3}>
        <Typo variant="body1" fontWeight={500} style={{ wordBreak: 'break-all' }}>
          {botSessionId}
        </Typo>
        <Bx mt={3} display="flex" justifyContent="flex-end">
          <Btn color="primary" size="large" variant="contained" startIcon={<FontAwesomeIcon icon={faCopy} />} onClick={copySession}>
            {copied ? 'Kopiert' : 'Kopieren'}
          </Btn>
        </Bx>
      </Bx>
    </Anohana>
  );

  return { actions, modal };
}
